import { isEmpty } from '../utils/util';
import { AfterViewInit, Directive, ElementRef, Input, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

@Directive({
	selector: '[gFormAsterisk]',
	standalone: false,
})
export class FormAsteriskDirective implements AfterViewInit {
	private readonly element = inject(ElementRef);

	static globalFormCounter = 0;
	@Input('gFormAsterisk') readonly form: any;
	@Input('gFormAsteriskRequired') readonly requiredFields: Set<string>;
	private readonly document = document;
	private formId: number;

	ngAfterViewInit() {
		// Wait for any schema forms to get ready
		setTimeout(() => {
			if (!Array.isArray(this.form) && typeof this.form !== 'object')
				throw new Error('(FormAsteriskDirective) Parameter is required');
			// Add unique identifier for the form element so we don't accidentally alter
			// the wrong form
			this.formId = FormAsteriskDirective.globalFormCounter;
			this.element.nativeElement.setAttribute('g-id', this.formId);
			FormAsteriskDirective.globalFormCounter++;
			// Add asterisks to required form fields under this <form> element
			this.addAsterisks();
		});
	}

	private addAsterisks() {
		const sources = Array.isArray(this.form) ? this.form : [this.form];
		for (const source of sources) {
			if (source instanceof UntypedFormGroup) this.processFormGroup(source);
			else if (this.isSchemaForm(source)) this.processSchemaForm(source);
		}
	}

	private processFormGroup(formGroup: UntypedFormGroup) {
		const controlKeys = Object.keys(formGroup.controls);
		for (const key of controlKeys) {
			if (!this.getRequiredStatus(key)) continue;
			this.markAsRequired(key, 'formControl');
		}
	}

	private processSchemaForm(schemaForm) {
		if (!Array.isArray(schemaForm.required) || isEmpty(schemaForm.required)) return;
		for (const key of schemaForm.required) this.markAsRequired(key, 'schemaForm');
	}

	private isSchemaForm(obj) {
		if (!obj) return false;
		if (typeof obj.$schema !== 'string' || isEmpty(obj.$schema)) return false;
		return true;
	}

	private getRequiredStatus(key) {
		if (!(this.requiredFields instanceof Set)) return false;
		return this.requiredFields.has(key);
	}

	private markAsRequired(key, type) {
		const form = `form[g-id='${this.formId}']`;
		let target;
		if (type === 'formControl') {
			// With FormControls, attempt to find the correct label based on "for" attribute and
			// use "g-id" custom attribute as fallback (used in checkout address forms)
			target = this.document.querySelector(`${form} label[for='${key}']`);
			if (!target) target = this.document.querySelector(`${form} label[g-id='${key}']`);
		} else if (type === 'schemaForm') {
			target = this.document.querySelector(`${form} [g-id='${key}'] label`);
		}
		if (!target) return;
		const className = target.className;
		target.className = isEmpty(className) ? 'is-required' : `${className} is-required`;
	}
}
